import { useFormContext } from 'react-hook-form';
import {
  useFlowsDispatch,
  useFlowsSelector,
} from '../../../../../../../common/src/utils/globalVariables';
import useAnswerForQuestion from '../../../../../hooks/useAnswerForQuestion';
import useFlowsHelper from '../../../../../hooks/useFlowsHelper';
import { useFlowsRouteParams } from '../../../../../hooks/useFlowsRouteParams';
import {
  removeAnswerForIndex,
  reshuffleAnswerIndices,
  updateAnswers,
} from '../../../../../store/redux/slices/flowsSlice';
import { FlowsQuestion } from '../../../../../types/FlowsTypes';
import { useDeleteEntity } from '../api/deleteEntity';
import useDuplicateGroupIds from './useDuplicateGroupIds';
import useUpdateItemAmount from './useUpdateItemAmount';

type UseRemoveTableEntity = {
  deleteEntity: (
    entityId: string,
    toBeRemovedIndex?: number,
    entityPartyId?: string,
  ) => void;
};

/**
 * This functions works as follows:
 * - Step 1: Execute API call to remove the entity
 * - Step 2: Remove all answer objects locally
 * - Step 3: Check for any dependant questions and adjust their answer accordingly
 *
 * @param question The amount question for the TABLE input type
 * @returns A function that mimicks the delete functionality for dynamic duplicate questions
 */
const useRemoveTableItem = (question: FlowsQuestion): UseRemoveTableEntity => {
  const { sessionId } = useFlowsRouteParams();
  const { getGroupByQuestionId } = useFlowsHelper();
  const flowsDispatch = useFlowsDispatch();
  const duplicateGroupIds = useDuplicateGroupIds(question.id);
  const flowAnswers = useFlowsSelector((state) => state.flows.answers);
  const { mutateAsync: deleteEntityApiCall } = useDeleteEntity();
  const updateItemAmount = useUpdateItemAmount(question);
  const getAnswerForQuestion = useAnswerForQuestion();

  const itemAmount =
    (getAnswerForQuestion(question.id, question.index)?.answer as number) || 0;

  // TODO: Make useFlowsAnswers hook
  const { setValue } = useFormContext();

  const deleteEntity = async (
    entityId: string,
    toBeRemovedIndex?: number,
    entityPartyId?: string,
  ) => {
    if (!sessionId) return console.error('No session id');
    if (toBeRemovedIndex == null) return;

    // -- STEP ONE
    // Do the API call
    await deleteEntityApiCall({ entityId, sessionId, partyId: entityPartyId });

    // -- STEP TWO
    // The changes were applied to BE, the answers can now be deleted locally
    duplicateGroupIds?.forEach((duplicateGroupId) => {
      flowsDispatch(
        removeAnswerForIndex({
          forIndex: toBeRemovedIndex,
          groupId: duplicateGroupId,
        }),
      );
    });

    // -- STEP THREE
    // Remove any answers for dependant questions
    question.rules.table?.index_question_ids?.forEach((questionId) => {
      const group = getGroupByQuestionId(questionId);
      const indexedAnswer = flowAnswers.find(({ id }) => id === questionId)
        ?.answer as number[];

      const newAnswerValue = indexedAnswer?.filter(
        (index) => index !== toBeRemovedIndex,
      );

      const newIndexedAnswer = {
        id: questionId,
        answer: newAnswerValue,
        groupId: group.id,
        tag: question.tag,
      };

      // TODO: Make useFlowsAnswers hook
      // - This is a temporary measure to save out time
      // - In theory there should be a useFlowsAnswer hook that takes
      // logic away from the useFlowsInputComponent hook
      setValue(questionId, newAnswerValue);
      flowsDispatch(updateAnswers(newIndexedAnswer));
    });

    // After all is done, reshuffle indicies
    duplicateGroupIds?.forEach((duplicateGroupId) => {
      flowsDispatch(
        reshuffleAnswerIndices({
          forIndex: toBeRemovedIndex,
          groupId: duplicateGroupId,
        }),
      );
    });

    updateItemAmount(itemAmount - 1);
  };

  return {
    deleteEntity,
  };
};

export default useRemoveTableItem;
